import { useMemo, useState } from "react";
import { Plus, Search, UserPlus, X } from "lucide-react";
import { T, inputStyle } from "../lib/theme";
import { AVG_MONTH_DAYS, daysSince, elapsed, fmtDate, isSunday, lastSunday, weekdayOf } from "../lib/dates";
import { MEETING_TYPES, typeOf, type Meeting, type MeetingType, type MemberSummary, type Slot } from "../lib/types";
import type { Settings } from "../lib/settings";
import type { SaveMeetingInput } from "../hooks/useData";
import { Card, Field, Header, Note, Sheet, SheetHeader, YouthTag } from "../components/ui";

type PickTarget = { kind: "speaker"; index: number } | { kind: "opening" } | { kind: "closing" };

const slotFrom = (a?: { member_id: string | null; guest_name: string | null; topic: string | null }): Slot | null =>
  a ? { memberId: a.member_id ?? undefined, guest: a.guest_name ?? undefined, topic: a.topic ?? "" } : null;

export function RecordScreen({
  members,
  meetings,
  settings,
  editing,
  prefillDate,
  saving,
  onSave,
  onCancelEdit,
}: {
  members: MemberSummary[];
  meetings: Meeting[];
  settings: Settings;
  editing: Meeting | null;
  prefillDate: string | null;
  saving: boolean;
  onSave: (input: SaveMeetingInput) => void;
  onCancelEdit: () => void;
}) {
  const [date, setDate] = useState(editing?.meeting_date ?? prefillDate ?? lastSunday());
  const [type, setType] = useState<MeetingType>(editing?.meeting_type ?? "regular");
  const [speakers, setSpeakers] = useState<Array<Slot | null>>(() => {
    if (!editing) return Array<Slot | null>(settings.speakerSlots).fill(null);
    const list = editing.assignments.filter((a) => a.role === "speaker").map((a) => slotFrom(a));
    return list.length ? list : [null];
  });
  const [opening, setOpening] = useState<Slot | null>(
    () => slotFrom(editing?.assignments.find((a) => a.role === "opening_prayer")),
  );
  const [closing, setClosing] = useState<Slot | null>(
    () => slotFrom(editing?.assignments.find((a) => a.role === "closing_prayer")),
  );
  const [picking, setPicking] = useState<PickTarget | null>(null);

  const cfg = typeOf(type);
  const clash = meetings.find((m) => m.meeting_date === date && m.id !== editing?.id);
  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  const applyPick = (val: Slot) => {
    if (!picking) return;
    if (picking.kind === "speaker") {
      const i = picking.index;
      setSpeakers((s) => s.map((x, j) => (j === i ? val : x)));
    }
    if (picking.kind === "opening") setOpening(val);
    if (picking.kind === "closing") setClosing(val);
    setPicking(null);
  };

  const save = () => {
    const assignments: SaveMeetingInput["assignments"] = [];
    if (cfg.speakers) {
      speakers.filter((s): s is Slot => Boolean(s)).forEach((s, i) =>
        assignments.push({
          member_id: s.memberId ?? null,
          guest_name: s.guest ?? null,
          role: "speaker",
          topic: s.topic?.trim() ? s.topic.trim() : null,
          position: i + 1,
        }),
      );
    }
    if (cfg.prayers) {
      if (opening)
        assignments.push({
          member_id: opening.memberId ?? null,
          guest_name: opening.guest ?? null,
          role: "opening_prayer",
          topic: null,
          position: 1,
        });
      if (closing)
        assignments.push({
          member_id: closing.memberId ?? null,
          guest_name: closing.guest ?? null,
          role: "closing_prayer",
          topic: null,
          position: 2,
        });
    }
    onSave({ meetingId: editing?.id ?? null, date, type, assignments });
  };

  return (
    <>
      <Header
        title={editing ? "Edit meeting" : "Record a meeting"}
        sub={editing ? `Recorded ${elapsed(editing.updated_at.slice(0, 10))}` : "Pick the date, then what happened"}
        right={
          editing && (
            <button
              onClick={onCancelEdit}
              style={{ color: "rgba(255,255,255,.8)", fontSize: 13.5, fontWeight: 500, padding: "6px 2px" }}
            >
              Cancel
            </button>
          )
        }
      />

      <div style={{ padding: 16 }}>
        <Card>
          <Field label="Date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </Field>
          {!isSunday(date) && (
            <Note tone="brass">
              {fmtDate(date)} is a {weekdayOf(date)}, not a Sunday.
            </Note>
          )}
          {clash && <Note tone="brass">{fmtDate(date)} is already recorded. Saving replaces it.</Note>}

          <Field label="Meeting type">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {MEETING_TYPES.map((t) => (
                <button
                  key={t.v}
                  onClick={() => setType(t.v)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    border: `1px solid ${type === t.v ? T.navy : T.line}`,
                    background: type === t.v ? T.navy : "#fff",
                    color: type === t.v ? "#fff" : T.ink,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>
        </Card>

        {!cfg.speakers && !cfg.prayers && (
          <Card>
            <div style={{ padding: "6px 2px", fontSize: 14, color: T.muted, lineHeight: 1.5 }}>
              Nothing to record for {cfg.label.toLowerCase()} — the ward isn't assigning speakers or prayers. Saving
              marks the Sunday as accounted for so it won't show as missing.
            </div>
          </Card>
        )}

        {cfg.speakers && (
          <Card>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Speakers
            </div>
            {speakers.map((s, i) => (
              <SlotRow
                key={i}
                value={s}
                member={s?.memberId ? byId.get(s.memberId) : undefined}
                placeholder={`Speaker ${i + 1}`}
                onPick={() => setPicking({ kind: "speaker", index: i })}
                onClear={() => setSpeakers((arr) => arr.map((x, j) => (j === i ? null : x)))}
                onTopic={(t) => setSpeakers((arr) => arr.map((x, j) => (j === i && x ? { ...x, topic: t } : x)))}
                showTopic
              />
            ))}
            <button
              onClick={() => setSpeakers((s) => [...s, null])}
              style={{ display: "flex", alignItems: "center", gap: 6, color: T.accent, fontSize: 13.5, fontWeight: 600, padding: "10px 2px 2px" }}
            >
              <Plus size={15} /> Add another speaker
            </button>
          </Card>
        )}

        {cfg.prayers && (
          <Card>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Prayers
            </div>
            <SlotRow
              value={opening}
              member={opening?.memberId ? byId.get(opening.memberId) : undefined}
              placeholder="Opening prayer"
              warnMonths={settings.prayerWarnMonths}
              onPick={() => setPicking({ kind: "opening" })}
              onClear={() => setOpening(null)}
            />
            <SlotRow
              value={closing}
              member={closing?.memberId ? byId.get(closing.memberId) : undefined}
              placeholder="Closing prayer"
              warnMonths={settings.prayerWarnMonths}
              onPick={() => setPicking({ kind: "closing" })}
              onClear={() => setClosing(null)}
            />
          </Card>
        )}

        <button
          onClick={save}
          disabled={saving}
          style={{
            width: "100%",
            padding: 15,
            borderRadius: 12,
            background: saving ? T.muted : T.navy,
            color: "#fff",
            fontSize: 15.5,
            fontWeight: 600,
            marginTop: 4,
          }}
        >
          {saving ? "Saving…" : editing ? "Save changes" : `Save ${fmtDate(date)}`}
        </button>
      </div>

      {picking && (
        <PickerSheet
          members={members}
          prayer={picking.kind !== "speaker"}
          warnMonths={settings.prayerWarnMonths}
          onPick={applyPick}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  );
}

function SlotRow({
  value,
  member,
  placeholder,
  warnMonths,
  onPick,
  onClear,
  onTopic,
  showTopic,
}: {
  value: Slot | null;
  member?: MemberSummary;
  placeholder: string;
  warnMonths?: number;
  onPick: () => void;
  onClear: () => void;
  onTopic?: (t: string) => void;
  showTopic?: boolean;
}) {
  if (!value) {
    return (
      <button
        onClick={onPick}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "12px 13px",
          borderRadius: 9,
          border: `1px dashed ${T.line}`,
          color: T.faint,
          fontSize: 14.5,
          marginBottom: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Plus size={16} /> {placeholder}
      </button>
    );
  }

  const recentPrayer =
    warnMonths !== undefined &&
    member?.last_prayer_date &&
    daysSince(member.last_prayer_date) < warnMonths * AVG_MONTH_DAYS;

  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 9, padding: "10px 12px", marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="serif" style={{ fontSize: 16.5, fontWeight: 500 }}>
            {value.guest ?? (member ? `${member.last_name}, ${member.first_name}` : "—")}
            {value.guest && (
              <span
                style={{
                  fontFamily: "Inter",
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: ".08em",
                  color: T.muted,
                  background: T.paper,
                  padding: "2px 5px",
                  borderRadius: 4,
                  marginLeft: 7,
                  verticalAlign: "middle",
                }}
              >
                GUEST
              </span>
            )}
          </div>
          {member && (
            <div style={{ fontSize: 11.5, color: T.faint, marginTop: 1 }}>
              last spoke {member.last_spoke_date ? elapsed(member.last_spoke_date) : "never"}
            </div>
          )}
        </div>
        <button onClick={onClear} aria-label="Clear" style={{ color: T.faint, padding: 4 }}>
          <X size={17} />
        </button>
      </div>

      {recentPrayer && member?.last_prayer_date && (
        <div style={{ marginTop: 8, fontSize: 12, color: T.brass, background: T.brassSoft, padding: "6px 9px", borderRadius: 6 }}>
          Prayed {elapsed(member.last_prayer_date)} — you may want someone else.
        </div>
      )}

      {showTopic && onTopic && (
        <input
          value={value.topic ?? ""}
          onChange={(e) => onTopic(e.target.value)}
          placeholder="Topic (optional)"
          style={{ ...inputStyle, marginTop: 8, fontSize: 14, padding: "8px 10px" }}
        />
      )}
    </div>
  );
}

function PickerSheet({
  members,
  prayer,
  warnMonths,
  onPick,
  onClose,
}: {
  members: MemberSummary[];
  prayer: boolean;
  warnMonths: number;
  onPick: (s: Slot) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const alpha = (a: MemberSummary, b: MemberSummary) =>
      a.last_name.localeCompare(b.last_name) || a.first_name.localeCompare(b.first_name);
    const needle = q.trim().toLowerCase();
    return members
      .filter((m) => m.status === "active")
      .filter(
        (m) =>
          !needle ||
          `${m.last_name} ${m.first_name}`.toLowerCase().includes(needle) ||
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(needle),
      )
      .sort((a, b) => {
        const ka = prayer ? a.last_prayer_date : a.last_spoke_date;
        const kb = prayer ? b.last_prayer_date : b.last_spoke_date;
        if (!ka && !kb) return alpha(a, b);
        if (!ka) return -1;
        if (!kb) return 1;
        const c = ka.localeCompare(kb);
        return c !== 0 ? c : alpha(a, b);
      })
      .slice(0, 80);
  }, [members, q, prayer]);

  return (
    <Sheet onClose={onClose} tall>
      <div
        style={{
          padding: "16px 18px 12px",
          borderBottom: `1px solid ${T.line}`,
          position: "sticky",
          top: 0,
          background: "#fff",
          zIndex: 2,
        }}
      >
        <SheetHeader title={prayer ? "Choose who prays" : "Choose a speaker"} onClose={onClose} />
        <div style={{ position: "relative" }}>
          <Search size={16} style={{ position: "absolute", left: 11, top: 12, color: T.faint }} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type a last name"
            style={{ ...inputStyle, paddingLeft: 34 }}
          />
        </div>
      </div>

      <div style={{ paddingBottom: 24 }}>
        {q.trim() && (
          <button
            onClick={() => onPick({ guest: q.trim() })}
            style={{
              width: "100%",
              textAlign: "left",
              padding: "13px 18px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              borderBottom: `1px solid ${T.line}`,
              background: T.accentSoft,
            }}
          >
            <UserPlus size={17} style={{ color: T.accent }} />
            <span style={{ fontSize: 14 }}>
              Add{" "}
              <strong className="serif" style={{ fontSize: 15.5 }}>
                {q.trim()}
              </strong>{" "}
              as a guest
            </span>
          </button>
        )}

        {list.map((m) => {
          const key = prayer ? m.last_prayer_date : m.last_spoke_date;
          const warn = prayer && m.last_prayer_date && daysSince(m.last_prayer_date) < warnMonths * AVG_MONTH_DAYS;
          return (
            <button
              key={m.id}
              onClick={() => onPick({ memberId: m.id })}
              className="row-press"
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px 18px",
                borderBottom: `1px solid ${T.line}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span className="serif" style={{ fontSize: 16.5 }}>
                {m.last_name}, {m.first_name}
                {m.speaker_type === "youth" && <YouthTag />}
              </span>
              <span
                style={{
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  color: warn ? T.brass : key ? T.muted : T.brass,
                  fontWeight: warn ? 600 : 400,
                }}
              >
                {key ? elapsed(key) : prayer ? "never prayed" : "never spoken"}
              </span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
